import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PagedList } from '@rma/dashboard/generic/domain/to-be-sorted';
import { RMA_DEFAULT_HTTP_OPTIONS } from '@rma/dashboard/generic/util/environment';
import { httpParamMapper } from '@rma/dashboard/generic/util/http';
import type { ExternalReviewSourceType } from '@rma/dashboard/reviews/public/api-external-reviews';
import { ProfileType, VerticalType } from '@rma/generic/domain/types';
import { appendToRmaReviewsApiUrl } from '@rma/generic/ng/util';
import { Observable } from 'rxjs';
import {
  ConvertReviewEnabledResponse,
  CreateOrUpdateConvertReviewDestinationRequest,
  CreateOrUpdateConvertReviewDestinationsRequest,
  CreateOrUpdateConvertReviewDestinationsResponse,
  CreateOrUpdateConvertReviewEnabledRequest,
  ProfileConvertReviewSetting,
} from '../models/google-conversion.model';

@Injectable({
  providedIn: 'root',
})
export class ReviewConversionApiService {
  private readonly http = inject(HttpClient);
  private readonly reviewsApiUrl = inject(appendToRmaReviewsApiUrl);

  public getMemberConvertReviewSettings(
    verticalType: VerticalType,
    profileType: ProfileType,
    profileCode: string,
    take: number,
    skip: number,
    externalReviewSource: ExternalReviewSourceType,
    memberCodes?: string[],
    name?: string,
  ): Observable<PagedList<ProfileConvertReviewSetting>> {
    const params = httpParamMapper({
      take,
      skip,
      externalReviewSource,
      memberCodes,
      name,
    });

    return this.http.get<PagedList<ProfileConvertReviewSetting>>(
      this.reviewsApiUrl(`${verticalType}/${profileType}/${profileCode}/ConvertReview/Settings`),
      { ...RMA_DEFAULT_HTTP_OPTIONS, params },
    );
  }

  public getOwnDestinationForProfile(
    verticalType: VerticalType,
    profileType: ProfileType,
    profileCode: string,
    externalReviewSource: ExternalReviewSourceType,
  ): Observable<ConvertReviewEnabledResponse> {
    return this.http.get<ConvertReviewEnabledResponse>(
      this.reviewsApiUrl(`${verticalType}/${profileType}/${profileCode}/${externalReviewSource}/ConvertReview/OwnDestination`),
      RMA_DEFAULT_HTTP_OPTIONS,
    );
  }

  public getConvertReviewEnableds(
    profileType: ProfileType,
    profileCode: string,
    externalReviewSource: ExternalReviewSourceType,
  ): Observable<ConvertReviewEnabledResponse> {
    return this.http.get<ConvertReviewEnabledResponse>(
      this.reviewsApiUrl(`${profileType}/${profileCode}/${externalReviewSource}/ConvertReview/Enableds`),
      RMA_DEFAULT_HTTP_OPTIONS,
    );
  }

  public createOrUpdateConvertReviewDestination(
    profileType: ProfileType,
    profileCode: string,
    externalReviewSource: ExternalReviewSourceType,
    request: CreateOrUpdateConvertReviewDestinationRequest,
  ) {
    return this.http.post(
      this.reviewsApiUrl(`${profileType}/${profileCode}/${externalReviewSource}/ConvertReview/Destination`),
      request,
      RMA_DEFAULT_HTTP_OPTIONS,
    );
  }

  public createOrUpdateConvertReviewDestinations(
    request: CreateOrUpdateConvertReviewDestinationsRequest,
  ): Observable<CreateOrUpdateConvertReviewDestinationsResponse> {
    return this.http.post<CreateOrUpdateConvertReviewDestinationsResponse>(
      this.reviewsApiUrl(`ConvertReview/Destinations`),
      request,
      RMA_DEFAULT_HTTP_OPTIONS,
    );
  }

  public createOrUpdateConvertReviewEnabled(
    profileType: ProfileType,
    profileCode: string,
    request: CreateOrUpdateConvertReviewEnabledRequest,
  ) {
    return this.http.post(this.reviewsApiUrl(`${profileType}/${profileCode}/ConvertReview/Enableds`), request, RMA_DEFAULT_HTTP_OPTIONS);
  }

  public applyDestinationToAll(
    verticalType: VerticalType,
    profileType: ProfileType,
    profileCode: string,
    externalReviewSource: ExternalReviewSourceType,
  ): Observable<null> {
    return this.http.post<null>(
      this.reviewsApiUrl(`${verticalType}/${profileType}/${profileCode}/${externalReviewSource}/ConvertReview/Destination/All`),
      {},
      RMA_DEFAULT_HTTP_OPTIONS,
    );
  }
}
